import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import ActionIcon from "../ActionIcon";
import MediaButton from "../MediaButton";
import AddToListModal from "components/AddToListModal";
import NotifyModal from "components/NotifyModal";
import CartEditModal from "components/CartEditModal";
import AddedPopup from "components/AddedPopup";
import styles from "./ItemIconSection.module.css";
import { enhancedStockInfo } from "../../utils/mockStockData";
import TrashIcon from "../../assets/Trash_Icon.svg";
import {
  useLike,
  useUnlike,
  useSubscribeNotification,
} from "../../hooks/useItemInteractions";
import { useContactId } from "../../hooks/useContactId";
import { useIsFreeTrial } from "../../hooks/useUser";
import {
  useAddListItem,
  useLists,
  useListItems,
  useRemoveListItem,
} from "../../hooks/useLists";
import { lowStockMessage } from "../../utils/stockStatus";
import {
  getVariantColorKey,
  resolveVariantColorAndSize,
} from "../../utils/variantParsing";
import { trackProductAddedToWishlist } from "../../utils/analytics";

function resolveIsLikedForContact(isLiked, contactId) {
  if (typeof isLiked === "boolean") return isLiked;
  if (isLiked && typeof isLiked === "object") {
    if (contactId == null) return false;
    return Boolean(isLiked[String(contactId)]);
  }
  return Boolean(isLiked);
}

export function ItemIconSection({
  item,
  isItemDetails,
  hideCart = false,
  disableCart = false,
  onCartClick,
  onNoteClick,
  hideDelete = false,
  onDelete,
  onClick,
  // Some list views (e.g. Purchased Items) still want the cart/more-icon
  // layout of a list row, but do not provide onDelete since
  // that list can't be manually edited. Force the list layout in that case.
  forceListLayout = false,
  // Consumers that render their own "Out of Stock" message elsewhere on the
  // card (e.g. a corner badge) can suppress this component's inline label
  // while still relying on `disableCart` to hide/disable the cart icon.
  hideOutOfStockLabel = false,
  // Same idea as hideOutOfStockLabel, but for the "low stock" label.
  hideLowStockLabel = false,
}) {
  const contactId = useContactId();
  const isFreeTrial = useIsFreeTrial();
  const [isLiked, setIsLiked] = useState(
    resolveIsLikedForContact(item?.isLiked, contactId)
  );
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [isNotifyActive, setIsNotifyActive] = useState(false);
  const [isCartEditModalOpen, setIsCartEditModalOpen] = useState(false);
  const [isListAdded, setIsListAdded] = useState(false);
  const [isFavoritesAdded, setIsFavoritesAdded] = useState(false);

  // Sync isLiked state when the item changes
  useEffect(() => {
    setIsLiked(resolveIsLikedForContact(item?.isLiked, contactId));
  }, [item?.stockId, item?.isLiked, contactId]);

  // Auto-dismiss list notification after 2 seconds
  useEffect(() => {
    if (isListAdded) {
      const timer = setTimeout(() => setIsListAdded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isListAdded]);

  // Auto-dismiss favorites notification after 2 seconds
  useEffect(() => {
    if (isFavoritesAdded) {
      const timer = setTimeout(() => setIsFavoritesAdded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isFavoritesAdded]);

  // Reset notify active state when item changes
  useEffect(() => {
    setIsNotifyActive(false);
  }, [item?.stockId]);

  const likeItem = useLike();
  const unlikeItem = useUnlike();
  const subscribeNotification = useSubscribeNotification();
  const addListItem = useAddListItem();
  const removeListItem = useRemoveListItem();
  const {
    itemType,
    memberPrice,
    title,
    category,
    isUnavailable,
    isLowStock,
    quantityAvailable,
  } = item || {};
  const isConfigurableItem =
    String(itemType || "")
      .trim()
      .toUpperCase() === "CONFIGURABLE";
  const isListContext = Boolean(onDelete) || forceListLayout;
  const isLiteratureItem = String(category || "")
    .toLowerCase()
    .includes("literature");
  const showReadAction = isLiteratureItem && item?.isReadable === true;
  // Only literature cards need the "already read" state up front; everyone
  // else's lists are irrelevant until they open the "Add to List" modal, so
  // this stops every card on the store grid from firing a background fetch.
  const { data: userLists = [] } = useLists({
    enabled: showReadAction || isAddToListModalOpen,
  });
  const booksReadList = useMemo(
    () =>
      userLists.find(
        (list) =>
          String(list?.type || "").toUpperCase() === "BOOKS_READ" ||
          String(list?.name || "")
            .trim()
            .toLowerCase() === "books read"
      ) || null,
    [userLists]
  );
  const { data: booksReadItems = [] } = useListItems(booksReadList?.listId);
  const booksReadEntry = useMemo(
    () =>
      booksReadItems.find((listItem) => {
        const listStockId =
          listItem?.stockId ?? listItem?.productId ?? listItem?.stockItemId;
        const currentStockId = item?.stockId ?? item?.productId;
        return String(listStockId ?? "") === String(currentStockId ?? "");
      }) || null,
    [booksReadItems, item?.stockId, item?.productId]
  );
  const isReadActive = Boolean(booksReadEntry);

  // Placeholder media codes until Store.CoreItemTypes are finalized by DB.
  const showMediaIcon = ["MEDIA", "AUDIO", "VIDEO"].includes(itemType);
  const wouldShowCart =
    memberPrice !== 0 && !showMediaIcon && !hideCart && !isFreeTrial;
  const showCartIcon = wouldShowCart && !disableCart;
  const showOutOfStockLabel =
    wouldShowCart && disableCart && !hideOutOfStockLabel;
  const showLowStockLabel =
    showCartIcon && Boolean(isLowStock) && !hideLowStockLabel;

  const enhancedItem = useMemo(
    () =>
      enhancedStockInfo.find(
        (i) => String(i.stockId) === String(item?.stockId)
      ),
    [item?.stockId]
  );

  // Parse the currently selected variant (if variantId exists)
  const selectedVariantInfo = useMemo(() => {
    if (!item?.variantId || !item?.variants?.length) {
      return null;
    }
    const selectedVariant = item.variants.find(
      (v) => String(v.variantId) === String(item.variantId)
    );
    if (!selectedVariant?.displayName) {
      return null;
    }
    const { color, size } = resolveVariantColorAndSize(selectedVariant);
    return { color, size };
  }, [item?.variantId, item?.variants]);

  const variantTuples = useMemo(() => {
    return (item?.variants || [])
      .filter((variant) => variant?.displayName)
      .map((variant) => {
        const { color, size } = resolveVariantColorAndSize(variant);
        // A variant is unavailable if it's marked unsellable OR the
        // per-variant stock enforcement (isInStock) says it's out of stock.
        // Missing isInStock (list-route responses don't populate it) is not
        // treated as out of stock - only an explicit `false` is.
        return {
          color,
          size,
          colorKey: getVariantColorKey(variant, color),
          swatchHex: variant?.swatchHex || null,
          swatchImage: variant?.swatchImage || null,
          variantId: variant?.variantId ?? null,
          variationId: variant?.variationId ?? null,
          isSellable:
            variant?.isSellable !== false && variant?.isInStock !== false,
          imageUrl: variant?.imageUrl ?? null,
        };
      })
      .filter((tuple) => tuple.color || tuple.size);
  }, [item?.variants]);

  const variantMatrix = useMemo(() => {
    const matrix = {};
    variantTuples.forEach((tuple) => {
      const key = `${tuple.color}::${tuple.size}`;
      matrix[key] = {
        variantId: tuple.variantId,
        variationId: tuple.variationId,
        isAvailable: tuple.isSellable,
      };
    });
    return matrix;
  }, [variantTuples]);

  const colorOptions = useMemo(() => {
    // If we have real API variants, use only those (don't mix in mock data)
    if (variantTuples.length > 0) {
      // Build a map of color to imageUrl using the first variant with that color
      const colorEntries = new Map();
      // A color is only sold out if every size under it is unavailable.
      const colorAvailability = new Map();
      variantTuples.forEach((tuple) => {
        if (!tuple.color) return;
        if (!colorEntries.has(tuple.colorKey)) {
          colorEntries.set(tuple.colorKey, {
            label: tuple.color,
            imageUrl: tuple.imageUrl ?? null,
            swatchHex: tuple.swatchHex,
            swatchImage: tuple.swatchImage,
          });
        }
        const wasAvailable = colorAvailability.get(tuple.colorKey);
        colorAvailability.set(
          tuple.colorKey,
          typeof wasAvailable === "boolean"
            ? wasAvailable || tuple.isSellable
            : tuple.isSellable
        );
      });

      return Array.from(colorEntries.entries()).map(([key, entry]) => ({
        ...entry,
        isSoldOut: !(colorAvailability.get(key) ?? true),
      }));
    }

    // If item has real variants from API but parsing didn't work, don't use mock data
    if (item?.variants && item.variants.length > 0) {
      // Return empty to prevent falling back to mock data
      return [];
    }

    // No real variants; only use mock data if configured item
    if (!isConfigurableItem) {
      return [];
    }

    if (enhancedItem?.detailColors?.length) {
      return enhancedItem.detailColors.map((option) => {
        if (typeof option === "string") {
          return { label: option, imageUrl: enhancedItem?.imageUrl };
        }

        return {
          label: option?.label || option?.value || "",
          imageUrl: option?.imageUrl || enhancedItem?.imageUrl,
        };
      });
    }

    if (enhancedItem?.colorOptions?.length) {
      return enhancedItem.colorOptions.map((option, index) => {
        if (typeof option === "string") {
          return {
            label: option,
            imageUrl:
              enhancedItem?.images?.[index]?.src || enhancedItem?.imageUrl,
          };
        }

        return {
          label: option?.label || option?.value || "",
          imageUrl:
            option?.imageUrl ||
            enhancedItem?.images?.[index]?.src ||
            enhancedItem?.imageUrl,
        };
      });
    }

    if (enhancedItem?.images?.length) {
      return enhancedItem.images.map((option, index) => ({
        label: option?.label || option?.name || `Option ${index + 1}`,
        imageUrl: option?.src || enhancedItem?.imageUrl,
      }));
    }

    return [];
  }, [enhancedItem, variantTuples, item?.variants, isConfigurableItem]);

  const sizeOptions = useMemo(() => {
    if (variantTuples.length > 0) {
      const seen = new Set();
      return variantTuples
        .filter((tuple) => tuple.size)
        .filter((tuple) => {
          const key = tuple.size;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((tuple) => ({
          label: tuple.size,
          isSoldOut: !tuple.isSellable,
        }));
    }

    // If item has real variants from API but parsing didn't work, don't use mock data
    if (item?.variants && item.variants.length > 0) {
      // Return empty to prevent falling back to mock data
      return [];
    }

    if (!isConfigurableItem) {
      return [];
    }

    return (enhancedItem?.sizeOptions || []).map((option) => {
      if (typeof option === "string")
        return { label: option, isSoldOut: false };
      return {
        label: option?.label || option?.value || "",
        isSoldOut: Boolean(option?.isSoldOut),
      };
    });
  }, [enhancedItem, variantTuples, item?.variants, isConfigurableItem]);

  const hasVariantChoices = colorOptions.length > 0 || sizeOptions.length > 0;

  const isLikePending = likeItem.isLoading || unlikeItem.isLoading;
  const isBooksReadPending = addListItem.isLoading || removeListItem.isLoading;

  const toggleBooksRead = () => {
    if (!booksReadList?.listId || !item?.stockId || isBooksReadPending) {
      return;
    }

    if (booksReadEntry) {
      // An optimistic row has no server id yet; deleting it would 404.
      if (String(booksReadEntry?.listProductId).startsWith("optimistic-")) {
        return;
      }
      removeListItem.mutate({
        listId: booksReadList.listId,
        listProductId: booksReadEntry?.listProductId,
        stockId: item.stockId,
      });
      return;
    }

    addListItem.mutate({
      listId: booksReadList.listId,
      stockId: item.stockId,
      variationId: item?.variationId ?? null,
      variantId: item?.variantId ?? null,
      sortOrder: item?.sortOrder ?? null,
    });
  };

  const handleIconClick = (type) => {
    switch (type) {
      case "like":
        if (item?.stockId == null || item?.stockId === "") {
          return;
        }
        if (isLikePending) {
          return;
        }
        if (isLiked) {
          setIsLiked(false);
          unlikeItem.mutate(item?.stockId, {
            onError: () => setIsLiked(true),
          });
        } else {
          setIsLiked(true);
          likeItem.mutate(item?.stockId, {
            onSuccess: () => trackProductAddedToWishlist(item),
            onError: () => setIsLiked(false),
          });
        }
        break;
      case "storeRead":
        toggleBooksRead();
        break;
      case "read":
        toggleBooksRead();
        break;
      case "more":
        setIsAddToListModalOpen(true);
        break;
      case "notify":
        setIsNotifyActive(!isNotifyActive);
        setIsNotifyModalOpen(false);
        subscribeNotification.mutate(item?.stockId, {
          onSuccess: () => {
            if (!isNotifyActive) setIsNotifyModalOpen(true);
          },
        });
        break;
      case "cart":
        setIsCartEditModalOpen(true);
        break;
      case "note":
        onNoteClick?.();
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.itemIconSection} onClick={onClick}>
      {!isListContext && (
        <ActionIcon
          iconType={isLiked ? "likeActive" : "like"}
          onClick={() => handleIconClick("like")}
          disabled={isLikePending}
        />
      )}

      {isListContext ? (
        <>
          {showMediaIcon ? (
            <MediaButton isModalOpen={false} mediaSettings={item} />
          ) : showCartIcon ? (
            <>
              {showLowStockLabel && (
                <span className={styles.lowStockLabel}>
                  {lowStockMessage(quantityAvailable)}
                </span>
              )}
              <ActionIcon
                iconType="cart"
                onClick={() => handleIconClick("cart")}
              />
            </>
          ) : showOutOfStockLabel ? (
            <span className={styles.outOfStockLabel}>Out of Stock</span>
          ) : null}
          {onNoteClick && (
            <ActionIcon
              iconType="note"
              onClick={() => handleIconClick("note")}
            />
          )}
          <ActionIcon iconType="more" onClick={() => handleIconClick("more")} />
        </>
      ) : showReadAction ? (
        <>
          <ActionIcon
            iconType={isReadActive ? "Read" : "storeRead"}
            onClick={() => handleIconClick(isReadActive ? "read" : "storeRead")}
            disabled={isBooksReadPending}
          />
          <ActionIcon iconType="more" onClick={() => handleIconClick("more")} />
        </>
      ) : title?.includes("Amway") ? (
        <ActionIcon iconType="Amway" onClick={() => handleIconClick("more")} />
      ) : (
        <ActionIcon iconType="more" onClick={() => handleIconClick("more")} />
      )}

      {!isListContext && showMediaIcon ? (
        <MediaButton isModalOpen={false} mediaSettings={item} />
      ) : !isListContext && showCartIcon ? (
        <>
          {showLowStockLabel && (
            <span className={styles.lowStockLabel}>
              {lowStockMessage(quantityAvailable)}
            </span>
          )}
          <ActionIcon iconType="cart" onClick={() => handleIconClick("cart")} />
        </>
      ) : !isListContext && showOutOfStockLabel ? (
        <span className={styles.outOfStockLabel}>Out of Stock</span>
      ) : null}

      {isUnavailable && (
        <ActionIcon
          iconType={isNotifyActive ? "notifyActive" : "notify"}
          onClick={() => handleIconClick("notify")}
        />
      )}

      {!hideDelete && onDelete && (
        <button
          type="button"
          className={styles.deleteIconButton}
          onClick={() => onDelete(item?.listProductId ?? item?.stockId)}
          aria-label="Delete item"
        >
          <img src={TrashIcon} alt="Delete" className={styles.deleteIcon} />
        </button>
      )}

      <AddToListModal
        isOpen={isAddToListModalOpen}
        onClose={() => setIsAddToListModalOpen(false)}
        onCancel={() => setIsAddToListModalOpen(false)}
        onAdd={(payload) => {
          // Check if adding to Favorites list
          const isFavoritesCategory =
            payload?.category?.type === "USER_FAVORITE";
          if (isFavoritesCategory) {
            setIsFavoritesAdded(true);
          } else {
            setIsListAdded(true);
          }
        }}
        item={item}
        showNewList={false}
        colorOptions={colorOptions.length > 0 ? colorOptions : undefined}
        sizeOptions={sizeOptions.length > 0 ? sizeOptions : undefined}
        variantMatrix={
          Object.keys(variantMatrix).length > 0 ? variantMatrix : undefined
        }
      />

      <NotifyModal
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
      />

      <CartEditModal
        isOpen={isCartEditModalOpen}
        onClose={() => setIsCartEditModalOpen(false)}
        onCancel={() => setIsCartEditModalOpen(false)}
        onSave={(updatedItem) => {
          onCartClick?.(updatedItem);
          setIsCartEditModalOpen(false);
        }}
        item={{
          stockId: item?.stockId,
          id: item?.id,
          title: item?.title,
          memberPrice: item?.memberPrice,
          quantity: item?.quantity ?? 1,
          quantityAvailable: item?.quantityAvailable,
          image: item?.image,
          isPreOrder: item?.isPreOrder,
          // Use selected variant info if available, otherwise use color/size from props
          color:
            hasVariantChoices && colorOptions.length > 0
              ? selectedVariantInfo?.color ||
                item?.color ||
                colorOptions[0].label
              : hasVariantChoices && item?.color !== undefined
              ? item.color
              : undefined,
          size:
            hasVariantChoices && sizeOptions.length > 0
              ? selectedVariantInfo?.size || item?.size || sizeOptions[0].label
              : hasVariantChoices && item?.size !== undefined
              ? item.size
              : undefined,
          variantMatrix,
        }}
        colorOptions={colorOptions}
        sizeOptions={sizeOptions.length > 0 ? sizeOptions : undefined}
      />

      <AddedPopup variants="list" isAdded={isListAdded} />
      <AddedPopup variants="favorites" isAdded={isFavoritesAdded} />
    </div>
  );
}

ItemIconSection.propTypes = {
  item: PropTypes.shape({
    stockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.number,
    title: PropTypes.string,
    itemType: PropTypes.string,
    category: PropTypes.string,
    memberPrice: PropTypes.number,
    author: PropTypes.string,
    isUnavailable: PropTypes.bool,
    quantity: PropTypes.number,
    color: PropTypes.string,
    size: PropTypes.string,
    image: PropTypes.string,
    isPreOrder: PropTypes.bool,
    isLowStock: PropTypes.bool,
    quantityAvailable: PropTypes.number,
  }).isRequired,
  isItemDetails: PropTypes.bool.isRequired,
  hideCart: PropTypes.bool,
  disableCart: PropTypes.bool,
  hideOutOfStockLabel: PropTypes.bool,
  hideLowStockLabel: PropTypes.bool,
  onCartClick: PropTypes.func,
  onNoteClick: PropTypes.func,
  hideDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  forceListLayout: PropTypes.bool,
};

export default ItemIconSection;
